import React, { useState } from 'react';
import gql from 'graphql-tag';

import type { OptionType } from '@atlaskit/select';
import { AsyncSelect } from '@atlaskit/select';
import { Box } from '@atlaskit/primitives';
import { cssMap } from '@atlaskit/css';
import type { MultiValue } from '@atlaskit/react-select';

import { getApolloClient, markErrorAsHandled } from '@confluence/graphql';
import { debouncePromise } from '@confluence/debounce';

import type { ActiveAudioContent } from '../../../../types/activeAudioContent';
import type {
	ContentSourcesPickerSearchQueryVariables,
	ContentSourcesPickerSearch,
} from '../../../__types__/ContentSourcesPickerSearchQuery';

import { getContentSearchCql } from './utils/getContentSearchCql';
import { isContentOption, getOptionItemsFromQueryData } from './utils/searchResultConversion';

// OptionType has label as string|number and I want to guarantee string
export type ContentOption = {
	value: string;
	label: string;
};

const styles = cssMap({
	container: {
		minHeight: '200px',
		minWidth: '250px',
	},
});

const ContentSourcesPickerSearchQuery = gql`
	query ContentSourcesPickerSearchQuery($cql: String, $limit: Int) {
		search: confluence_search(cql: $cql, first: $limit) {
			nodes {
				content {
					id
					title
				}
			}
		}
	}
`;

export type ContentSourcesPickerProps = {
	onChange: (newAdditionalResources: ActiveAudioContent[]) => void;
};

export const ContentSourcesPicker = ({ onChange }: ContentSourcesPickerProps) => {
	const [searchLoading, setSearchLoading] = useState(false);

	// Loads the search results in the select dropdown
	const handleSearch = debouncePromise(async (searchQuery: string) => {
		setSearchLoading(true);
		try {
			const response = await getApolloClient().query<
				ContentSourcesPickerSearch,
				ContentSourcesPickerSearchQueryVariables
			>({
				errorPolicy: 'all',
				query: ContentSourcesPickerSearchQuery,
				variables: {
					cql: getContentSearchCql(searchQuery),
					limit: 5,
				},
			});
			if (response.errors) {
				response.errors.forEach((error) => {
					markErrorAsHandled(error);
				});
			}
			return [
				{
					options: getOptionItemsFromQueryData(response.data),
				},
			];
		} catch (error) {
			markErrorAsHandled(error);
		} finally {
			setSearchLoading(false);
		}
	}, 500);

	const handleChange = (newValue: MultiValue<OptionType>) => {
		const newActiveAudioContent: ActiveAudioContent[] = newValue
			.filter(isContentOption)
			.map((value) => ({ id: value.value, contentTitle: value.label }));

		onChange(newActiveAudioContent);
	};

	return (
		<Box xcss={styles.container}>
			<AsyncSelect
				inputId="audio-briefing-sources-select"
				testId="audio-briefing-sources-select"
				loadOptions={handleSearch}
				isLoading={searchLoading}
				isMulti
				onChange={handleChange}
				menuPortalTarget={document.body}
				// Select is a unique case where this is semantically correct
				// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
				styles={{
					menuPortal: (base) => ({ ...base, zIndex: 800 }),
				}}
				isSearchable
				placeholder="Choose additional resources"
			/>
		</Box>
	);
};
